import { TFunction } from 'i18next';
import { Option } from 'lib/components/dropdown/Dropdown';
import { IImgProps } from 'lib/components/formik/FormikImageUpload/types';
import { NAPLETON_AUTOMOTIVE_RESELLER_ID } from 'lib/const/SuperAdminConstants';

export interface CompanyValues {
  businessName: string;
  firstNameBusiness: string;
  lastNameBusiness: string;
  businessPhone1: string;
  businessPhone2?: string;
  businessFax?: string;
  businessEmail: string;
  businessUrl?: string;
  allowReplies: boolean;
  preloadTeleprompterScripts: boolean;
  quoteSettings: string;
  reactionsPreference: number;
  region?: Option;
  markVideosAsSent?: string;
  credit700Url?: string;
  mapIframeEmbedCode: string;
  autominerGroupId?: string;
  resellerId?: number;
  leadsNotificationsAccess: boolean;
  customerLogo?: string | IImgProps;
  shareCooldownSeconds?: number;
}

interface ErrorValues {
  businessName?: string;
  firstNameBusiness?: string;
  lastNameBusiness?: string;
  businessPhone1?: string;
  businessPhone2?: string;
  businessFax?: string;
  businessEmail?: string;
  businessUrl?: string /* SUS-796 changes */;
  mapIframeEmbedCode?: string;
}

const getMessages = (t: TFunction) => ({
  firstNameBusiness: [
    t('Please enter your first name.'),
    t('First name cannot be empty.'),
    t(
      'First name can only contain letters, numbers, parenthesis, dashes(-), plus(+) and periods(.).'
    ),
    t(
      'First name can not contain more than 100 characters.'
    ) /* SUS-796 changes */,
  ],
  lastNameBusiness: [
    t('Please enter your last name.'),
    t(
      'Last name can only contain letters, numbers, parenthesis, dashes(-), plus(+) and periods(.).'
    ),
    t(
      'Last name can not contain more than 100 characters.'
    ) /* SUS-796 changes */,
  ],
  businessEmail: [t('Please enter a valid email address.')],
  businessPhone1: [
    t('Your primary phone number cannot be empty.'),
    t('Please enter a phone number equal or longer than 7 characters.'),
    t(
      'Phone number 1 can only contain letters, numbers, parenthesis, dashes(-), plus(+) and periods(.)'
    ),
  ],
  businessPhone2: [
    t('Please enter a phone number equal or longer than 7 characters.'),
    t(
      'Phone number 2 can only contain letters, numbers, parenthesis, dashes(-), plus(+) and periods(.)'
    ),
  ],
  businessName: [
    t('Please enter your business name.'),
    t(
      'Business name can only contain letters, numbers, spaces and/or (-+@,.&_) special characters can be used.'
    ),
    t(
      'Business name can not contain more than 100 characters.'
    ) /* SUS-796 changes */,
  ],
  businessFax: [
    t(
      'Fax can only contain letters, numbers, parenthesis, dashes(-), plus(+) and periods(.).'
    ),
  ],
  businessUrl: [
    t('Business Url can not contain more than 100 characters.'),
  ] /* SUS-796 changes */,
});

export const validateCompany = (values: CompanyValues, t: TFunction) => {
  //const nameRegex = /^[a-zA-Z0-9()+\-.\s']+$/i;
  const businessRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ0-9+\-,.&_@\s']+$/i;
  const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ0-9()+\-.\s']+$/i;
  const emailRegex = /^[0-9a-z\\._-]+@[0-9a-z-]+\..+$/i;
  const phoneRegex = /^[0-9a-z()+\-.\s]+$/i;
  const errors: ErrorValues = {};
  if (!values.businessName) {
    errors.businessName = getMessages(t).businessName[0];
  } else if (!businessRegex.test(values.businessName)) {
    errors.businessName = getMessages(t).businessName[1];
  } else if (values.businessName && values.businessName.length > 100) {
    /* SUS-796 changes */
    errors.businessName = getMessages(t).businessName[2];
  }

  if (!values.firstNameBusiness) {
    errors.firstNameBusiness = getMessages(t).firstNameBusiness[0];
  } else if (!nameRegex.test(values.firstNameBusiness)) {
    errors.firstNameBusiness = getMessages(t).firstNameBusiness[2];
  } else if (
    values.firstNameBusiness &&
    values.firstNameBusiness.length > 100
  ) {
    /* SUS-796 changes */
    errors.firstNameBusiness = getMessages(t).firstNameBusiness[3];
  }

  if (
    !values.lastNameBusiness &&
    Number(values.resellerId) !== NAPLETON_AUTOMOTIVE_RESELLER_ID
  ) {
    errors.lastNameBusiness = getMessages(t).lastNameBusiness[0];
  }
  if (values.lastNameBusiness) {
    if (!nameRegex.test(values.lastNameBusiness)) {
      errors.lastNameBusiness = getMessages(t).lastNameBusiness[1];
    } else if (
      values.lastNameBusiness &&
      values.lastNameBusiness.length > 100
    ) {
      /* SUS-796 changes */
      errors.lastNameBusiness = getMessages(t).lastNameBusiness[2];
    }
  }

  if (!values.businessEmail) {
    errors.businessEmail = getMessages(t).businessEmail[0];
  } else if (!emailRegex.test(values.businessEmail)) {
    errors.businessEmail = getMessages(t).businessEmail[1];
  }

  if (!values.businessPhone1) {
    errors.businessPhone1 = getMessages(t).businessPhone1[0];
  } else if (values.businessPhone1.length < 7) {
    errors.businessPhone1 = getMessages(t).businessPhone1[1];
  } else if (!phoneRegex.test(values.businessPhone1)) {
    errors.businessPhone1 = getMessages(t).businessPhone1[2];
  }

  if (values.businessPhone2 && values.businessPhone2.length > 0) {
    if (values.businessPhone2.length < 7) {
      errors.businessPhone2 = getMessages(t).businessPhone2[0];
    } else if (!phoneRegex.test(values.businessPhone2)) {
      errors.businessPhone2 = getMessages(t).businessPhone2[1];
    }
  }
  /* SUS-796 changes */
  if (values.businessUrl && values.businessUrl.length > 100) {
    errors.businessUrl = getMessages(t).businessUrl[0];
  }

  return errors;
};
